import React, { useRef, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import UserSelectors from 'Stores/User/Selectors'
import AuthSelectors from 'Stores/Auth/Selectors'
import UserActions from 'Stores/User/Actions'
import Styles from './TransactionTimeline.module.css'
import { IFText, IFButton, IFModal, IFSkeleton } from 'Components'
import { Colors } from 'Theme'
import { Actions, Subjects } from 'Enums/permissionKeys'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

const PHASE_COLORS = {
  charging: '#1D9E75',
  grace: '#EF9F27',
  idle: '#D85A30',
}

// Charge notifications are linked to their session only from this date onward.
// For sessions before it, an empty timeline is expected (they predate tracking),
// so we show an informational note instead of "no notifications". Set this to the
// production deploy date of the notification session-linking change.
const NOTIFICATION_TRACKING_START = '2026-07-29'

const TransactionTimeline = ({
  transaction,
  selectedUserId,
  editingAdminRole,
  timeline,
  fetchTransactionTimelineRequestState,
  fetchTransactionTimeline,
}) => {
  const { t } = useTranslation()
  const modalRef = useRef(null)

  const canView = editingAdminRole?.can(
    Actions.VIEW_NOTIFICATIONS,
    Subjects.USERS,
  )

  const phases = useMemo(() => {
    const start = transaction?.startedAt
    const end = transaction?.details?.endedAt
    if (!start || !end) return null
    // The charging-session window (start -> endedAt) is the active charging time.
    // Parking (grace + idle) happens after charging stops, so it is added on top.
    const chargingMins = Math.max(0, moment(end).diff(moment(start), 'minutes'))
    const parkingItems = (transaction?.costBreakdown?.items || []).filter(
      (i) => i.type?.toUpperCase?.() === 'PARKING_TIME',
    )
    const hoursOf = (item) => Number(item.billedVolume ?? item.volume ?? 0)
    const graceMins = Math.round(
      parkingItems.filter((i) => !i.inclVat).reduce((s, i) => s + hoursOf(i), 0) * 60,
    )
    const idleMins = Math.round(
      parkingItems.filter((i) => i.inclVat).reduce((s, i) => s + hoursOf(i), 0) * 60,
    )
    return { chargingMins, graceMins, idleMins }
  }, [transaction])

  const fmt = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`

  const onView = () => {
    modalRef.current?.show()
    fetchTransactionTimeline(selectedUserId, transaction._id || transaction.id)
  }

  if (!canView || !phases) return null

  const isLoading =
    fetchTransactionTimelineRequestState === RequestState.LOADING
  const notifications = timeline?.notifications || []
  const sessionDate = transaction.startedAt || transaction.details?.endedAt
  // Tracking begins partway through the start day, so sessions on that same day
  // (before deploy) also lack notifications — treat the whole cutoff day, and
  // everything before it, as "before tracking" by extending the boundary a day.
  const isBeforeTracking =
    sessionDate &&
    moment(sessionDate).isBefore(
      moment(NOTIFICATION_TRACKING_START, 'YYYY-MM-DD').add(1, 'day'),
    )

  const total =
    phases.chargingMins + phases.graceMins + phases.idleMins || 1
  const widthOf = (mins) => `${(mins / total) * 100}%`
  const startMs = moment(transaction.startedAt).valueOf()
  const pinPct = (n) => {
    const p = ((moment(n.createdAt).valueOf() - startMs) / (total * 60000)) * 100
    return Math.min(100, Math.max(0, p))
  }

  const LegendItem = ({ phaseKey, label, mins }) => (
    <span className={Styles.LegendItem}>
      <span
        className={Styles.PhaseDot}
        style={{ background: PHASE_COLORS[phaseKey] }}
      />
      <IFText className={Styles.LegendText}>{`${label} · ${fmt(mins)}`}</IFText>
    </span>
  )

  return (
    <div className={Styles.Container} onClick={(e) => e.stopPropagation()}>
      <IFButton
        size="sm"
        isFill={false}
        color={Colors.UserPagePrimary}
        text={t('UserPage.Timeline.View')}
        onClick={onView}
      />

      <IFModal
        ref={modalRef}
        title={t('UserPage.Timeline.Title')}
        maxWidth="sm"
        fullWidth
      >
        <div className={Styles.ModalBody}>
          <div className={Styles.Legend}>
            <LegendItem
              phaseKey="charging"
              label={t('UserPage.Timeline.Charging')}
              mins={phases.chargingMins}
            />
            <LegendItem
              phaseKey="grace"
              label={t('UserPage.Timeline.Grace')}
              mins={phases.graceMins}
            />
            <LegendItem
              phaseKey="idle"
              label={t('UserPage.Timeline.Idle')}
              mins={phases.idleMins}
            />
          </div>

          <div className={Styles.TimelineArea}>
            <div className={Styles.PinsRow}>
              {!isLoading &&
                notifications.map((n, idx) => {
                  const pct = pinPct(n)
                  // Keep edge pins inside the track (no horizontal overflow/scroll):
                  // right-align near the end, left-align near the start, else center.
                  const shift = pct >= 95 ? '-100%' : pct <= 5 ? '0%' : '-50%'
                  return (
                    <span
                      key={n._id || n.id || idx}
                      className={Styles.Pin}
                      style={{ left: `${pct}%`, transform: `translateX(${shift})` }}
                      title={n.title?.en || n.title?.ar || n.type}
                    >
                      {idx + 1}
                    </span>
                  )
                })}
            </div>

            <div className={Styles.Bar}>
              <div
                style={{
                  width: widthOf(phases.chargingMins),
                  background: PHASE_COLORS.charging,
                }}
              />
              <div
                style={{
                  width: widthOf(phases.graceMins),
                  background: PHASE_COLORS.grace,
                }}
              />
              <div
                style={{
                  width: widthOf(phases.idleMins),
                  background: PHASE_COLORS.idle,
                }}
              />
            </div>

            <div className={Styles.TimeLabels}>
              <IFText className={Styles.TimeLabel} style={{ color: Colors.gray }}>
                {moment(transaction.startedAt).format('MMM DD, HH:mm')}
              </IFText>
              <IFText className={Styles.TimeLabel} style={{ color: Colors.gray }}>
                {moment(startMs + total * 60000).format('MMM DD, HH:mm')}
              </IFText>
            </div>
          </div>

          <IFText className={Styles.SectionTitle}>
            {t('UserPage.Timeline.NotificationsSent')}
          </IFText>

          {isLoading ? (
            <div className={Styles.SkeletonList}>
              {[0, 1, 2].map((i) => (
                <IFSkeleton key={i} variant="text" width="80%" height={20} />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            isBeforeTracking ? (
              <div className={Styles.InfoAlert}>
                <IFText className={Styles.InfoAlertText}>
                  {t('UserPage.Timeline.NotTracked')}
                </IFText>
              </div>
            ) : (
              <IFText className={Styles.EmptyText} style={{ color: Colors.gray }}>
                {t('UserPage.Timeline.NoNotifications')}
              </IFText>
            )
          ) : (
            <div className={Styles.NotifList}>
              {notifications.map((n, idx) => (
                <div className={Styles.NotifRow} key={n._id || n.id || idx}>
                  <span className={Styles.NotifIndex}>{idx + 1}</span>
                  <IFText className={Styles.NotifTitle}>
                    {n.title?.en || n.title?.ar || n.type}
                  </IFText>
                  <IFText
                    className={Styles.NotifTime}
                    style={{ color: Colors.gray }}
                  >
                    {moment(n.createdAt).format('MMM DD, YYYY LTS')}
                  </IFText>
                </div>
              ))}
            </div>
          )}
        </div>
      </IFModal>
    </div>
  )
}

TransactionTimeline.propTypes = {
  transaction: PropTypes.object,
}

function mapDispatchToProps(dispatch) {
  return {
    fetchTransactionTimeline: (userId, transactionId) =>
      dispatch(UserActions.fetchTransactionTimeline(userId, transactionId)),
    clearTransactionTimeline: () =>
      dispatch(UserActions.clearTransactionTimeline()),
  }
}

const mapStateToProps = (state) => ({
  selectedUserId: UserSelectors.getSelectedUserId(state),
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  timeline: UserSelectors.getTransactionTimeline(state),
  fetchTransactionTimelineRequestState:
    UserSelectors.getFetchTransactionTimelineRequestState(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(TransactionTimeline)
